import styled from 'styled-components';
import { useUserLanguage } from '@/customHooks';
import { GetManagerMassiveCommunicationsQuery } from '@/graphql/generated';
import { ButtonWithDropDownMenu } from '../molecules';
import textsData from '@/textData/CommunicationsListTexts.json';

type Communication = NonNullable<GetManagerMassiveCommunicationsQuery['managerMassiveCommunications']>[number];

type CommunicationsListTableProps = {
  communications: Communication[];
};

const StyledGridTable = styled.div`
  padding-top: ${({ theme }) => theme.spacing.lg};
  padding-bottom: ${({ theme }) => theme.spacing.lg};
  border-radius: 12px;
  display: grid;
  grid-template-columns: 48px 2fr 1fr 1fr 64px;
`;

const StyledGridHeader = styled.div`
  display: contents;
  & > div {
    font-weight: bold;
    text-align: left;
    padding: 15px;
    border-top: 1px solid grey;
    border-bottom: 1px solid gray;
  }
`;

const StyledGridRow = styled.div`
  display: contents;
  & > div {
    padding: 15px;
    font-size: 14px;
    padding-top: 1rem;
    padding-bottom: 1rem;
    align-self: stretch;
    place-content: center;
    border-bottom: solid 1px lightgray;
    background-color: ${({ theme }) => theme.colors.light};
  }
  &:nth-of-type(odd) > div {
    background-color: ${({ theme }) => theme.colors.background2};
  }
`;

const StatusDelivered = styled.span`
  color: ${({ theme }) => theme.colors.primary1};
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
`;

const formatDate = (date: string) => {
  return new Date(date).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const SentIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="10" fill="currentColor" opacity="0.2" />
    <path d="M12 6l6 6-6 6M6 12h12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
  </svg>
);

export const CommunicationsListTable = ({ communications }: CommunicationsListTableProps) => {
  const userLanguage = useUserLanguage();
  const texts = userLanguage === 'es' ? textsData.es : textsData.en;

  return (
    <StyledGridTable>
      <StyledGridHeader>
        <div></div>
        <div>{texts.table.subject}</div>
        <div>{texts.table.sentAt}</div>
        <div>{texts.table.status}</div>
        <div></div>
      </StyledGridHeader>
      {communications.map((comm: Communication) => (
        <StyledGridRow key={comm.id}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <input type="checkbox" />
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
              <span style={{ color: 'var(--color-primary-1)' }}>
                <SentIcon />
              </span>
              <strong>{comm.title}</strong>
            </div>
            <div style={{ fontSize: '12px', color: '#666' }}>
              {comm.content?.slice(0, 60)}
              {comm.content && comm.content.length > 60 ? '...' : ''}
            </div>
          </div>
          <div>{formatDate(comm.createdAt)}</div>
          <div>
            <StatusDelivered>
            ✓ {texts.statusDelivered}
            {comm.recipientCount != null && (
              <span style={{ marginLeft: 4, opacity: 0.9 }}>
                ({(texts as { recipientsCount?: string }).recipientsCount?.replace('{{count}}', String(comm.recipientCount)) ?? `${comm.recipientCount} recipients`})
              </span>
            )}
          </StatusDelivered>
          </div>
          <div>
            <ButtonWithDropDownMenu
              options={[]}
              position="left"
            />
          </div>
        </StyledGridRow>
      ))}
    </StyledGridTable>
  );
};
